(function () {
  'use strict';

  const Hai = window.Hai || (window.Hai = {});
  const SECTION_SELECTOR = '[data-section-type="collection"]';
  const PIN_HANDLE_PARAM = 'pin_handle';
  const sectionStateMap = new WeakMap();
  let lifecycleBound = false;

  function addCleanup(state, callback) {
    if (!state || typeof callback !== 'function') return;
    state.cleanups.push(callback);
  }

  function defineSortDropdown() {
    if (customElements.get('hai-sort-dropdown')) return;

    customElements.define('hai-sort-dropdown', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.haiSortDropdownBound === 'true') return;
        this.dataset.haiSortDropdownBound = 'true';

        this.details = this.querySelector('details');
        this.hiddenInput = this.querySelector('input[name="sort_by"]');
        this.options = Array.from(this.querySelectorAll('.hai-collection__sort-option'));
        this.summaryText = this.querySelector('.hai-collection__sort-selectedText');

        if (!this.details || !this.hiddenInput) return;

        this.handleOptionClick = (event) => {
          const option = event.currentTarget;
          const value = option.dataset.value;
          event.preventDefault();

          if (this.hiddenInput.value !== value) {
            this.hiddenInput.value = value;
            this.options.forEach((item) => item.classList.remove('is-active'));
            option.classList.add('is-active');

            if (this.summaryText) {
              this.summaryText.textContent = option.textContent.trim();
            }

            this.hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
          }

          this.details.removeAttribute('open');
        };

        this.handleBodyClick = (event) => {
          if (!this.contains(event.target)) {
            this.details.removeAttribute('open');
          }
        };

        this.options.forEach((option) => {
          option.addEventListener('click', this.handleOptionClick);
        });

        document.body.addEventListener('click', this.handleBodyClick);
      }

      disconnectedCallback() {
        if (Array.isArray(this.options) && this.handleOptionClick) {
          this.options.forEach((option) => {
            option.removeEventListener('click', this.handleOptionClick);
          });
        }

        if (this.handleBodyClick) {
          document.body.removeEventListener('click', this.handleBodyClick);
        }

        delete this.dataset.haiSortDropdownBound;
      }
    });
  }

  function getPinnedHandle(url) {
    try {
      const target = new URL(url || window.location.href, window.location.origin);
      return (target.searchParams.get(PIN_HANDLE_PARAM) || '').trim().toLowerCase();
    } catch (error) {
      return '';
    }
  }

  function appendPinnedHandle(url, sourceUrl) {
    const target = new URL(url, window.location.origin);
    const pinnedHandle = getPinnedHandle(sourceUrl);

    if (pinnedHandle) {
      target.searchParams.set(PIN_HANDLE_PARAM, pinnedHandle);
    } else {
      target.searchParams.delete(PIN_HANDLE_PARAM);
    }

    return target.toString();
  }

  function pinProductInGrid(grid, sourceUrl) {
    if (!grid) return;

    const pinnedHandle = getPinnedHandle(sourceUrl);
    if (!pinnedHandle) return;

    const pinnedItem = Array.from(grid.querySelectorAll('[data-product-handle]')).find((item) => {
      return (item.dataset.productHandle || '').trim().toLowerCase() === pinnedHandle;
    });

    if (!pinnedItem || grid.firstElementChild === pinnedItem) return;
    grid.insertBefore(pinnedItem, grid.firstElementChild);
  }

  function initSection(section) {
    if (!section || sectionStateMap.has(section)) return;

    const form = section.querySelector('[data-collection-form]');
    const grid = section.querySelector('[data-collection-grid]');
    const status = section.querySelector('[data-collection-status]');
    const sectionId = section.dataset.sectionId;
    const basePath = section.dataset.collectionUrl || window.location.pathname;

    if (!form || !grid || !sectionId) return;

    const state = { cleanups: [] };
    sectionStateMap.set(section, state);

    const getFilterControls = () => {
      return {
        toggle: section.querySelector('[data-filter-toggle]'),
        wrapper: section.querySelector('[data-collection-filters-wrapper]')
      };
    };

    const setFiltersOpen = (isOpen) => {
      const controls = getFilterControls();
      if (!controls.toggle || !controls.wrapper) return;

      controls.toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      controls.wrapper.classList.toggle('is-visible', Boolean(isOpen));
    };

    const syncResetLink = () => {
      const resetLink = section.querySelector('[data-collection-reset]');
      if (!resetLink) return;

      const baseHref = resetLink.dataset.baseHref || resetLink.getAttribute('href') || basePath;
      resetLink.dataset.baseHref = baseHref;
      resetLink.href = appendPinnedHandle(baseHref);
    };

    const appendSectionParams = (url) => {
      const target = new URL(url, window.location.origin);
      target.searchParams.set('section_id', sectionId);
      target.searchParams.set('sections_url', window.location.pathname);
      return target.toString();
    };

    const cleanUrl = (url) => {
      const target = new URL(url, window.location.origin);
      target.searchParams.delete('section_id');
      target.searchParams.delete('sections_url');
      return `${target.pathname}${target.search}${target.hash}`;
    };

    const toggleLoading = (isLoading) => {
      if (status) {
        status.hidden = !isLoading;
      }
      section.classList.toggle('is-loading', Boolean(isLoading));
    };

    const renderResponse = (htmlText, destinationUrl) => {
      const currentControls = getFilterControls();
      const keepFiltersOpen = currentControls.toggle?.getAttribute('aria-expanded') === 'true';
      const temp = document.createElement('div');
      temp.innerHTML = htmlText;
      const newSection = temp.querySelector(`#shopify-section-${sectionId}`);
      if (!newSection) return;

      const newForm = newSection.querySelector('[data-collection-form]');
      const newGrid = newSection.querySelector('[data-collection-grid]');
      const newStatus = newSection.querySelector('[data-collection-status]');

      if (newForm) {
        form.innerHTML = newForm.innerHTML;
      }

      setFiltersOpen(keepFiltersOpen);

      if (newGrid) {
        grid.innerHTML = newGrid.innerHTML;
      }

      if (status && newStatus) {
        status.hidden = newStatus.hasAttribute('hidden');
      }

      syncResetLink();
      pinProductInGrid(grid, destinationUrl);

      if (Hai.lazyImages && typeof Hai.lazyImages.init === 'function') {
        Hai.lazyImages.init();
      }

      const urlWithoutParams = cleanUrl(destinationUrl);
      window.history.replaceState({}, '', urlWithoutParams);

      document.dispatchEvent(new CustomEvent('hai:collection:updated', {
        detail: {
          sectionId,
          url: urlWithoutParams
        }
      }));
    };

    const fetchAndRender = async (requestUrl) => {
      toggleLoading(true);

      try {
        const response = await fetch(requestUrl, {
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) {
          throw new Error(`Collection fetch failed: ${response.status}`);
        }

        const htmlText = await response.text();
        renderResponse(htmlText, requestUrl);
      } catch (error) {
        console.error('Collection render error:', error);
      } finally {
        toggleLoading(false);
      }
    };

    const buildUrlFromForm = () => {
      const formData = new FormData(form);
      const url = new URL(basePath, window.location.origin);
      url.search = '';

      const params = new URLSearchParams(formData);
      params.forEach((value, key) => {
        if (value === '' || value == null) return;
        url.searchParams.append(key, value);
      });

      return appendPinnedHandle(url.toString());
    };

    const onFormChange = (event) => {
      const target = event.target;
      if (!target) return;

      if (target.matches('[data-collection-sort]') || target.matches('input[type="checkbox"]')) {
        event.preventDefault();
        fetchAndRender(appendSectionParams(buildUrlFromForm()));
      }
    };

    const onFormSubmit = (event) => {
      event.preventDefault();
      fetchAndRender(appendSectionParams(buildUrlFromForm()));
    };

    const onSectionClick = (event) => {
      const filterToggle = event.target.closest('[data-filter-toggle]');
      if (filterToggle && section.contains(filterToggle)) {
        const controls = getFilterControls();
        if (!controls.toggle || !controls.wrapper) return;

        event.preventDefault();
        event.stopPropagation();

        const isExpanded = controls.toggle.getAttribute('aria-expanded') === 'true';
        setFiltersOpen(!isExpanded);
        return;
      }

      const filterControlLink = event.target.closest(
        '[data-collection-filter-remove], [data-collection-filter-clear], [data-collection-reset]'
      );
      if (filterControlLink && section.contains(filterControlLink)) {
        event.preventDefault();
        fetchAndRender(appendSectionParams(appendPinnedHandle(filterControlLink.href)));
        return;
      }

      const paginationLink = event.target.closest('[data-collection-pagination] a');
      if (!paginationLink) return;

      event.preventDefault();
      fetchAndRender(appendSectionParams(appendPinnedHandle(paginationLink.href)));
    };

    const onDocumentClick = (event) => {
      const controls = getFilterControls();
      if (!controls.toggle || !controls.wrapper) return;

      const isExpanded = controls.toggle.getAttribute('aria-expanded') === 'true';
      if (!isExpanded) return;

      if (controls.toggle.contains(event.target) || controls.wrapper.contains(event.target)) {
        return;
      }

      setFiltersOpen(false);
    };

    const onDocumentKeydown = (event) => {
      if (event.key !== 'Escape') return;

      const controls = getFilterControls();
      if (!controls.toggle || !controls.wrapper) return;

      const isExpanded = controls.toggle.getAttribute('aria-expanded') === 'true';
      if (!isExpanded) return;

      setFiltersOpen(false);
    };

    form.addEventListener('change', onFormChange);
    form.addEventListener('submit', onFormSubmit);
    section.addEventListener('click', onSectionClick);
    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onDocumentKeydown);

    addCleanup(state, () => {
      form.removeEventListener('change', onFormChange);
      form.removeEventListener('submit', onFormSubmit);
      section.removeEventListener('click', onSectionClick);
      document.removeEventListener('click', onDocumentClick);
      document.removeEventListener('keydown', onDocumentKeydown);
      delete section.dataset.haiEnhanced;
    });

    section.dataset.haiEnhanced = 'true';
    syncResetLink();
    pinProductInGrid(grid);
  }

  function destroySection(section) {
    const state = sectionStateMap.get(section);
    if (!state) return;

    state.cleanups.forEach((cleanup) => {
      try {
        cleanup();
      } catch (error) {
        console.error('Main collection cleanup error:', error);
      }
    });

    sectionStateMap.delete(section);
  }

  function initInRoot(root) {
    const target = root || document;
    if (!target) return;

    if (typeof target.matches === 'function' && target.matches(SECTION_SELECTOR)) {
      initSection(target);
    }

    if (typeof target.querySelectorAll === 'function') {
      target.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
        initSection(section);
      });
    }
  }

  function destroyInRoot(root) {
    const target = root || document;
    if (!target) return;

    if (typeof target.matches === 'function' && target.matches(SECTION_SELECTOR)) {
      destroySection(target);
    }

    if (typeof target.querySelectorAll === 'function') {
      target.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
        destroySection(section);
      });
    }
  }

  function bindLifecycleEvents() {
    if (lifecycleBound) return;
    lifecycleBound = true;

    document.addEventListener('shopify:section:load', (event) => {
      initInRoot(event.target);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      destroyInRoot(event.target);
    });
  }

  defineSortDropdown();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initInRoot(document);
      bindLifecycleEvents();
    });
  } else {
    initInRoot(document);
    bindLifecycleEvents();
  }

  Hai.collection = {
    init: initInRoot,
    destroy: destroyInRoot,
    bindSection: initSection
  };
})();
